import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/data/content";
import { adminApi, getToken } from "@/lib/adminApi";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (getToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await adminApi.login(email.trim(), password);
      toast.success("Welcome back.");
      nav("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid place-items-center px-5 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={BRAND.logo} alt="Vernika" className="mx-auto h-16 w-16 rounded-full ring-1 ring-[#e7c15a]/40" />
          <div className="mt-5 font-serif-lux text-2xl text-gold-gradient tracking-[0.12em]">VERNIKA DESIGNS</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#b8b8b8] mt-2">Admin Portal</div>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-2 mb-6 text-[#e7c15a]">
            <Lock size={16} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.25em]">Sign In</span>
          </div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[#b8b8b8] mb-2">Email</label>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0d0d0d] border border-white/10 px-4 py-3.5 text-sm text-[#fafafa] outline-none focus:border-[#e7c15a] focus:ring-1 focus:ring-[#e7c15a]/40"
            autoComplete="email"
          />
          <label className="block text-xs uppercase tracking-[0.2em] text-[#b8b8b8] mt-5 mb-2">Password</label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0d0d0d] border border-white/10 px-4 py-3.5 text-sm text-[#fafafa] outline-none focus:border-[#e7c15a] focus:ring-1 focus:ring-[#e7c15a]/40"
            autoComplete="current-password"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-8 w-full bg-[#e7c15a] px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium text-[#050505] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-[#666]">Restricted to Vernika Designs administrators.</p>
      </div>
    </div>
  );
}
